import React, { useEffect, useState, useRef } from "react";
import { Modal, Button, Form, Placeholder } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import DropFiles from "./DropFiles.js";
import Search from "./Search.js";
import * as cases from '../services/api/cases.js';
import * as attachments from '../services/api/attachments.js';
import { getVideoFrame, getVideoMetadata, sortVideoFilesByName, checkFilenameFormat, getVideoInfo} from "../services/util/video.js";
import { uploadFiles } from '../services/redux/fileUploadSlice';
import { addNotification } from '../services/redux/notificationsSlice';
import mixpanel from 'mixpanel-browser';
import * as Sentry from '@sentry/react';

const FileItem  = ({filename, size, duration, thumbnail, index, handleChangeFileOrder, handleRemoveFile}) => {
    return (
        <FileItemContainer>
            <FileInfoGroup>
                {thumbnail ? (
                    <FileItemThumbnail src={thumbnail} />
                ) : (
                    <Placeholder xs={12} style={{ height: '30px', width: '53px', borderRadius: '7px' }} />
                )}
                <span style={{width: '200px'}}>{filename.length > 25 ? `${filename.substring(0, 25)}...` : filename}</span>
                <span>{duration ? Math.round(duration/60) : 0} m</span>
                <span>{Math.round(size/1000000)} MB</span>
            </FileInfoGroup>
            <FileButtonGroup>
                <ImgButton
                    src="/images/icons/expand-contract.svg"
                    style={{rotate: '180deg'}}
                    onClick={() => {handleChangeFileOrder(index, 'up')}}
                    />
                <ImgButton
                    src="/images/icons/expand-contract.svg"
                    onClick={() => {handleChangeFileOrder(index, 'down')}}
                    />
                <ImgButton 
                    src="/images/icons/x-symbol.svg"
                    onClick={() => {handleRemoveFile(index)}}
                    />
            </FileButtonGroup>
        </FileItemContainer>
    )
}

const FileItemContainer = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    height: auto;
    gap: 10px;
    border-radius: 10px;
    justify-content: space-between;
    align-items: center;
    padding: 7px 10px; 
    box-shadow: 0 0 10px rgba(0,0,0,.1);
`;

const FileItemThumbnail = styled.img`
    width: auto;
    height: 30px;
    border-radius: 7px;
`;

const FileInfoGroup = styled.div`
    display: flex;
    flex-direction: row;
    gap: 20px;
    min-width: 70%;
    align-items: center;
`;

const FileButtonGroup = styled.div`
    display: flex;
    flex-direction: row;
    gap: 10px;
`;

const InfoSection = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 15px 10px;
    gap: 10px;
    border-radius: 8px;
    background-color: #f8f9fa;
    margin-top: 10px;
    margin-bottom: 10px;
`;

const InfoRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
    color: #555;
    line-height: 1.4;
`;

const InfoIcon = styled.img`
    width: 20px;
    height: 20px;
    opacity: ${props => props.grayed ? '0.6' : '1'};
`;

const UploadModal = ({ show, onHide }) => {

    const dispatch = useDispatch();

    const [fileList, setFileList] = useState([]);
    const [fileSizeList, setFileSizeList] = useState([]);
    const [fileLengthList, setFileLengthList] = useState([]);
    const [caseName, setCaseName] = useState('');
    const [caseDescription, setCaseDescription] = useState('');
    const [thumbnails, setThumbnails] = useState([]);
    const [noTitleError, setNoTitleError] = useState(false);
    const [user_metadataList, setUserMetadataList] = useState([]);
    const [hasOrchidCamera, setHasOrchidCamera] = useState(false);

    const user = useSelector(state => state.user.value);
    console.log('user', user);


    useEffect(() => {
        // Check if any video is from an Orchid camera
        if(user_metadataList.length > 0) {
            const isOrchid = user_metadataList.some(metadata => 
                metadata.camera && metadata.camera.toLowerCase().includes('orchid'));
            setHasOrchidCamera(isOrchid);
        } else {
            setHasOrchidCamera(false);
        }
    }, [user_metadataList]);

    const handleClearFiles = () => {
        setFileList([]);
        setFileSizeList([]);
        setFileLengthList([]);
        setCaseName('');
        setCaseDescription('');
        setThumbnails([]);
        setUserMetadataList([]);
    }

    const handleAddFiles = async (handles) => {
        // "handles" is an array of FileSystemHandles
        let newHandles = [];
        for (let i = 0; i < handles.length; i++) {
            let duplicate = false;
            let mp4 = false;
            if (handles[i].name.toLowerCase().endsWith('.mp4')) {
                mp4 = true;
            }
            for (let j = 0; j < fileList.length; j++) {
                if (handles[i].name === fileList[j].name) {
                    duplicate = true;
                    break;
                }
            }
            if (!duplicate && mp4) {
                newHandles.push(handles[i]);
            }
        }
        console.log('newHandles', newHandles);
        const combinedHandles = [...fileList, ...newHandles];
        // Optionally sort: first retrieve Files (via getFile) then sort, then map back to handles.
        const filesForSorting = await Promise.all(combinedHandles.map(handle => handle.getFile()));
        const sortedFiles = await sortVideoFilesByName(filesForSorting);
        const sortedHandles = sortedFiles.map(file => {
            return combinedHandles.find(handle => handle.name === file.name);
        });
        setFileList(sortedHandles);
        // Update fileSizeList, fileLengthList, and thumbnails using File objects from handles.
        Promise.all(sortedHandles.map(handle => handle.getFile()))
          .then(filesArray => {
              setFileSizeList(filesArray.map(file => file.size));
              return filesArray;
          })
          .then(filesArray => Promise.all(filesArray.map(file => getVideoInfo(file))))
          .then((metadataList) => {
            console.log(metadataList);
            metadataList.forEach(metadata => {
                console.log(`Duration: ${metadata.duration}, Width: ${metadata.width}, Height: ${metadata.height}`);
            });
            setFileLengthList(metadataList.map((metadata) => metadata.duration));
            setThumbnails(metadataList.map((metadata) => metadata.thumbnail));
            setUserMetadataList(metadataList.map((metadata) => {
                    return {
                        'duration': metadata.duration,
                        'width': metadata.width,
                        'height': metadata.height,
                        'bitrate': metadata.bitrate,
                        'camera': metadata.camera
                    }
                }));
          })
          .catch((error) => {
              console.error('Error getting video metadata:', error);
          });
    }

    const handleRemoveFile = (ind) => {
        let newFileList = [...fileList];
        let newFileSizeList = [...fileSizeList];
        let newFileLengthList = [...fileLengthList];
        let newThumbnails = [...thumbnails];
        let newUser_MetadataList = [...user_metadataList];
        newFileList.splice(ind, 1);
        newFileSizeList.splice(ind, 1);
        newFileLengthList.splice(ind, 1);
        newThumbnails.splice(ind, 1);
        newUser_MetadataList.splice(ind, 1);
        setFileList(newFileList);
        setFileSizeList(newFileSizeList);
        setFileLengthList(newFileLengthList);
        setThumbnails(newThumbnails);
        setUserMetadataList(newUser_MetadataList);
    }

    const handleChangeFileOrder = (ind, direction) => {
        let newFileList = [...fileList];
        let newFileSizeList = [...fileSizeList];
        let newFileLengthList = [...fileLengthList];
        let newThumbnails = [...thumbnails];
        let newUser_MetadataList = [...user_metadataList];
        if (direction === 'up') {
            if (ind > 0) {
                [newFileList[ind-1], newFileList[ind]] = [newFileList[ind], newFileList[ind-1]];
                [newFileSizeList[ind-1], newFileSizeList[ind]] = [newFileSizeList[ind], newFileSizeList[ind-1]];
                [newFileLengthList[ind-1], newFileLengthList[ind]] = [newFileLengthList[ind], newFileLengthList[ind-1]];
                [newThumbnails[ind-1], newThumbnails[ind]] = [newThumbnails[ind], newThumbnails[ind-1]];
                [newUser_MetadataList[ind-1], newUser_MetadataList[ind]] = [newUser_MetadataList[ind], newUser_MetadataList[ind-1]];
            }
        } else if (direction === 'down') {
            if (ind < newFileList.length - 1) {
                [newFileList[ind+1], newFileList[ind]] = [newFileList[ind], newFileList[ind+1]];
                [newFileSizeList[ind+1], newFileSizeList[ind]] = [newFileSizeList[ind], newFileSizeList[ind+1]];
                [newFileLengthList[ind+1], newFileLengthList[ind]] = [newFileLengthList[ind], newFileLengthList[ind+1]];
                [newThumbnails[ind+1], newThumbnails[ind]] = [newThumbnails[ind], newThumbnails[ind+1]];
                [newUser_MetadataList[ind+1], newUser_MetadataList[ind]] = [newUser_MetadataList[ind], newUser_MetadataList[ind+1]];
            }
        }
        setFileList(newFileList);
        setFileSizeList(newFileSizeList);
        setFileLengthList(newFileLengthList);
        setThumbnails(newThumbnails);
        setUserMetadataList(newUser_MetadataList);
    }

    const handleUpload = () => {
        if (fileList.length > 0 && caseName.length > 0)
        {      
            uploadVideo(fileList, caseName, caseDescription, fileLengthList.reduce((acc, fileLength) => acc + fileLength, 0), user_metadataList[0]);
            handleClearFiles();
            onHide();
            mixpanel.track('Clicked Upload Video', {'Source': 'Upload Dialogue'})
        } else {
            setNoTitleError(true);
        }
    }

    const uploadVideo = async (handles, caseName, caseDescription, videoLength, user_metadata) => {
        let attachment_id = null;
        let hashes = handles.map(() => '1234567890abcdef1234567890abcdef1234567890abcdef1234567890abcdef');
        cases.createCase(
            {
                'case_name': caseName, 
                'description': caseDescription,
            }
        )
        .then(data => data['id'])
        .then(case_id => attachments.createAttachment(
            {
                'case_id': case_id,
                'subtitle': caseName,
                'user_metadata': {
                    'camera': user_metadata.camera,
                    'duration': videoLength,
                },
            }
        ))
        .then(data => {
            attachment_id = data['id']
            return attachment_id
        })
        .then(attachment_id => {
            // Upload the thumbnail
            if (thumbnails[0]) {
                fetch(thumbnails[0])
                    .then(res => res.blob())
                    .then((blob) => {
                        attachments.uploadThumbnail(attachment_id, blob);
                    });
            }

            console.log('dispatching uploadFiles');
            let totalSize = fileSizeList.reduce((acc, fileSize) => acc + fileSize, 0);
            
            // Add notification first
            dispatch(addNotification({
                id: attachment_id, 
                title: 'Your upload has started.', 
                variant: 'info', 
                message: 'For best results, please keep this tab open and ensure your device does not go to sleep.'
            }));
            
            // Dispatch uploadFiles separately with serializable parameters only
            dispatch(uploadFiles({ 
                attachment_id, 
                caseName, 
                caseDescription, 
                files: handles,  // The fileHandlesMap in fileUploadSlice will store these
                checksums: hashes, 
                totalSize, 
                videoLength
            }));

            // Track the upload
            try {
                mixpanel.track('Started Video Upload', {
                    'Attachment ID': attachment_id, 
                    'File Count': handles.length, 
                    'Total Size': totalSize, 
                    'Video Length': videoLength, 
                    'Resolution': `${user_metadata.width}x${user_metadata.height}`, 
                    'Bitrate': user_metadata.bitrate, 
                    'Camera': user_metadata.camera
                });
            } catch (error) {
                Sentry.captureException(error);
            }
        })
    };

    return (
        <Modal centered show={show} onHide={onHide} size='lg' >
            <Modal.Header closeButton>
                <Modal.Title>Upload Video</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <DropZoneContainer>
                    Click below or drag-and-drop to choose video files (mp4) to upload:
                    <DropFiles handleChange={handleAddFiles}/>
                    <div style={{flexDirection: "column", justifyContent: "space-between"}}>
                    {fileList.length} file(s), &nbsp;
                    {Math.round(fileLengthList.reduce((acc, fileLength) => acc + fileLength, 0)/60)} minutes, &nbsp;
                    {Math.round(fileSizeList.reduce((acc, fileSize) => acc + fileSize, 0)/100000000)/10} GB.
                    <Button variant='link' onClick={handleClearFiles} style={{marginTop: '-5px'}}>
                        Clear Files
                    </Button>
                    </div>
                </DropZoneContainer>
                <FileListArea>
                    {fileList.map((file, ind) => {
                        return <FileItem 
                                key={ind}
                                filename={file.name}
                                size={fileSizeList[ind]}
                                duration={fileLengthList[ind]}
                                thumbnail={thumbnails[ind]}
                                index={ind}
                                handleChangeFileOrder={handleChangeFileOrder}
                                handleRemoveFile={handleRemoveFile}
                            />
                    })}
                </FileListArea>
                <InfoSection>
                    <InfoRow>
                        <InfoIcon src="/images/icons/alert-square.svg" grayed={true} style={{marginRight:'5px'}}/>
                        <span> These files will be combined into a single case video.</span>
                    </InfoRow>
                    {hasOrchidCamera && user.specialization == 'cardiothoracic' && (
                        <InfoRow>
                            <InfoIcon src="/images/icons/ai_icon.png" style={{height:'25px', width:'25px', marginTop:'-2.5px'}}/>
                            <span>This case will be edited by Orchid AI to remove all non-procedure segments.</span>
                        </InfoRow>
                    )}
                </InfoSection>
                <LabelArea>
                    <Form.Control
                        type="text"
                        placeholder="Enter a short, descriptive title (required)"
                        value={caseName}
                        isInvalid={noTitleError}
                        onChange={(e) => {setCaseName(e.target.value); setNoTitleError(false)}}
                    />
                    <Form.Control
                        as="textarea"
                        rows={2}
                        placeholder="Case Notes"
                        value={caseDescription}
                        onChange={(e) => setCaseDescription(e.target.value)}
                    />
                </LabelArea>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Close
                </Button>
                <Button variant="primary" onClick={() => {handleUpload()}} disabled={fileLengthList.some((fileLength) => fileLength === 0) || fileLengthList.length === 0}>
                    Upload
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

const DropZoneContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 150px;
`;

const LabelArea = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    gap: 10px;
`;

const TagArea = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    height: 100%;
    gap: 10px;
`;

const FileListArea = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 160px;
    gap: 5px;
    overflow-y: scroll;
    padding: 10px;
`;

const ImgButton = styled.img`
    height: 24px;
    cursor: pointer;

    &:hover {
        opacity: 0.7;
    }
`;

export default UploadModal;